<div class="migration-campaigns-container">
  <mat-card>
    <mat-card-content>
      <form [formGroup]="migrationForm" class="migration-form">
        <!-- CSV File Upload Section -->
        <div class="upload-section">
          <h3>Upload CSV File</h3>
          <mat-card-subtitle
            >Maximum 500 rows (including header)</mat-card-subtitle
          >
          <ngx-dropzone
            (change)="onFileSelect($event)"
            [accept]="'.csv'"
            [multiple]="false"
          >
            <ngx-dropzone-label>
              <mat-icon>cloud_upload</mat-icon>
              <p>Drop CSV file here or click to browse</p>
              <small>Only CSV files are accepted</small>
            </ngx-dropzone-label>
            @for (file of selectedFiles; track file.name) {
              <ngx-dropzone-preview
                [removable]="true"
                (removed)="onFileRemove(file)"
              >
                <ngx-dropzone-label
                  >{{ file.name }} ({{
                    file.size | number
                  }}
                  bytes)</ngx-dropzone-label
                >
              </ngx-dropzone-preview>
            }
          </ngx-dropzone>

          <!-- CSV Template Download -->
          <div class="template-section">
            <mat-icon>info</mat-icon>
            <span>Don't have a CSV template? </span>
            <lib-button
              (buttonClick)="downloadTemplate()"
              color="primary"
              icon="download"
            >
              Download Template
            </lib-button>
          </div>
        </div>

        <!-- Migration Options Section -->
        <div class="options-section">
          <h3>Migration Options</h3>

          <div class="option-row">
            <mat-slide-toggle
              formControlName="revokeOldCampaigns"
              color="primary"
            >
              <span class="toggle-label">Revoke Old Campaigns</span>
            </mat-slide-toggle>
            <mat-icon
              matTooltip="When enabled, old campaigns will be automatically revoked after successful migration of each user (Supported 1 old campaign)"
              class="info-icon"
            >
              help_outline
            </mat-icon>
          </div>

          <div class="option-row">
            <mat-slide-toggle
              formControlName="enableDebitPoints"
              color="primary"
            >
              <span class="toggle-label">Enable Debit Points</span>
            </mat-slide-toggle>
            <mat-icon
              matTooltip="Allow debit point operations during migration (Only first campaign will be debited [campaign_id_1])"
              class="info-icon"
            >
              help_outline
            </mat-icon>
          </div>

          <div class="option-row">
            <mat-slide-toggle
              formControlName="enableCreditPoints"
              color="primary"
            >
              <span class="toggle-label">Enable Credit Points</span>
            </mat-slide-toggle>
            <mat-icon
              matTooltip="Allow credit point operations during migration (Only first campaign will be credited [campaign_id_1])"
              class="info-icon"
            >
              help_outline
            </mat-icon>
          </div>

          <div class="option-row">
            <mat-slide-toggle
              formControlName="enableAddCard"
              color="primary"
            >
              <span class="toggle-label">Enable Add Card</span>
            </mat-slide-toggle>
            <mat-icon
              matTooltip="Add card for user during migration (Join campaign)"
              class="info-icon"
            >
              help_outline
            </mat-icon>
          </div>

          <div class="option-row">
            <mat-slide-toggle
              formControlName="enableAddTags"
              color="primary"
            >
              <span class="toggle-label">Enable Add Tags</span>
            </mat-slide-toggle>
            <mat-icon
              matTooltip="Add custom tags to all users during migration"
              class="info-icon"
            >
              help_outline
            </mat-icon>
          </div>

          <!-- Custom Tags Input - Show only when enableAddTags is enabled -->
          @if (migrationForm.get('enableAddTags')?.value) {
            <div class="custom-tags-section">
              <app-multi-labels
                formControlName="customTags"
                placeholder="Custom Tags"
                class="full-width"
              >
              </app-multi-labels>
              <div class="help-text">
                <mat-icon>info</mat-icon>
                <span>Enter tags in key:value format</span>
              </div>
            </div>
          }
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <lib-button
            color="primary"
            [disabled]="!canStartMigration()"
            (buttonClick)="startMigration()"
            icon="play_arrow"
          >
            Start Migration
          </lib-button>

          <lib-button (buttonClick)="resetForm()" icon="refresh">
            Reset
          </lib-button>
        </div>
      </form>
    </mat-card-content>
  </mat-card>

  <!-- Migration Progress Section -->
  @if (isMigrationRunning || migrationCompleted || migrationEverStarted) {
    <mat-card class="progress-card">
      <mat-card-header>
        <mat-card-title>Migration Progress</mat-card-title>
      </mat-card-header>

      <mat-card-content>
        <!-- Progress Bar -->
        <div class="progress-section">
          <div class="progress-header">
            <span>Overall Progress</span>
            <span>{{ successPercentage }}%</span>
          </div>
          <mat-progress-bar
            mode="determinate"
            [value]="successPercentage"
            [color]="getProgressBarColor()"
          >
          </mat-progress-bar>
          <div class="progress-details">
            <span
              >{{ migrationProgress.successful }} /
              {{ migrationProgress.total }} campaigns successfully migrated</span
            >
          </div>
        </div>

        <!-- Status Information -->
        <div class="status-section">
          <div class="status-item">
            <mat-icon
              [color]="migrationProgress.successful > 0 ? 'primary' : ''"
              >check_circle</mat-icon
            >
            <span>Successful: {{ migrationProgress.successful }}</span>
          </div>
          <div class="status-item">
            <mat-icon [color]="migrationProgress.failed > 0 ? 'warn' : ''"
              >error</mat-icon
            >
            <span>Failed: {{ migrationProgress.failed }}</span>
          </div>
          <div class="status-item">
            <mat-icon [color]="migrationProgress.skipped > 0 ? 'accent' : ''"
              >skip_next</mat-icon
            >
            <span>Skipped: {{ migrationProgress.skipped }}</span>
          </div>
        </div>

        <!-- Migration Log - Always visible once migration starts or has logs -->
        @if (migrationLogs.length > 0 || isMigrationRunning ||
        migrationCompleted || migrationEverStarted) {
          <div class="log-section">
            <div class="log-header">
              <h4>Migration Log</h4>
              @if (migrationLogs.length > 0) {
                <div class="log-controls">
                  @if (isMonitoringActive()) {
                    <mat-icon
                      color="accent"
                      matTooltip="Monitoring Active"
                      class="pulse-icon"
                      >wifi</mat-icon
                    >
                  }
                  <lib-button
                    variant="icon"
                    color="accent"
                    (buttonClick)="clearLogs()"
                    matTooltip="Clear logs"
                    icon="clear_all"
                  >
                  </lib-button>
                  @if (isMonitoringActive()) {
                    <lib-button
                      variant="icon"
                      color="warn"
                      (buttonClick)="stopMigrationMonitoring()"
                      matTooltip="Stop monitoring"
                      icon="stop"
                    >
                    </lib-button>
                  }
                </div>
              }
            </div>
            <div class="log-container" #logContainer>
              @for (log of migrationLogs; track log.timestamp) {
                <div class="log-entry" [ngClass]="'log-' + log.level">
                  <span class="log-timestamp">{{
                    log.timestamp | date : "HH:mm:ss"
                  }}</span>
                  <span class="log-message">{{ log.message }}</span>
                </div>
              }
              @if (migrationLogs.length === 0) {
                <div class="log-empty">
                  <mat-icon color="disabled">info</mat-icon>
                  <span>Migration logs will appear here...</span>
                </div>
              }
            </div>
          </div>
        }

        <!-- Download Results -->
        @if (migrationCompleted) {
          <div class="results-section">
            <h4>Migration Results</h4>
            <div class="result-actions">
              <lib-button
                color="primary"
                (buttonClick)="downloadResults()"
                icon="download"
              >
                Download Migration Report
              </lib-button>
              @if (errorReportS3Url || migrationProgress.failed > 0) {
                <lib-button
                  color="accent"
                  (buttonClick)="downloadErrors()"
                  icon="error_outline"
                >
                  Download Error Report
                </lib-button>
              }
            </div>
          </div>
        }
      </mat-card-content>
    </mat-card>
  }
</div>
